import { useState, useEffect } from 'react';
import { MachineType, OperatingSystem } from '@tryiris-ai/common.protojs/agent/v1/agent';

export interface DeviceInfo {
  type: 'desktop' | 'laptop' | 'tablet' | 'mobile' | 'unknown';
  confidence: 'high' | 'medium' | 'low';
  features: {
    hasHover: boolean;
    hasFinePointer: boolean;
    hasCoarsePointer: boolean;
    screenSize: 'small' | 'medium' | 'large';
    orientation: 'portrait' | 'landscape';
    touchSupport: boolean;
    devicePixelRatio: number;
    batteryAPI: boolean;
    aspectRatio: number;
    userAgentHints: {
      mobile: boolean;
      platform: string;
      model: string;
    };
    maxTouchPoints: number;
  };
}

class DeviceDetector {
  private static instance: DeviceDetector;
  private deviceInfo: DeviceInfo | null = null;

  public static getInstance(): DeviceDetector {
    if (!DeviceDetector.instance) {
      DeviceDetector.instance = new DeviceDetector();
    }
    return DeviceDetector.instance;
  }

  /**
   * Main method to detect device type
   */
  public detectDevice(): DeviceInfo {
    if (this.deviceInfo) {
      return this.deviceInfo;
    }

    const features = this.getDeviceFeatures();
    const type = this.determineDeviceType(features);
    const confidence = this.calculateConfidence(features, type);

    this.deviceInfo = {
      type,
      confidence,
      features
    };

    return this.deviceInfo;
  }

  private getDeviceFeatures() {
    return {
      hasHover: this.supportsHover(),
      hasFinePointer: this.supportsFinePointer(),
      hasCoarsePointer: this.supportsCoarsePointer(),
      screenSize: this.getScreenSize(),
      orientation: this.getOrientation(),
      touchSupport: this.supportsTouchEvents(),
      devicePixelRatio: this.getDevicePixelRatio(),
      batteryAPI: this.supportsBatteryAPI(),
      aspectRatio: this.getAspectRatio(),
      userAgentHints: this.getUserAgentHints(),
      maxTouchPoints: this.getMaxTouchPoints()
    };
  }

  /**
   * Check if device supports hover (indicates mouse/trackpad)
   */
  private supportsHover(): boolean {
    return window.matchMedia('(hover: hover)').matches;
  }

  /**
   * Check if device has fine pointer (mouse, trackpad, stylus)
   */
  private supportsFinePointer(): boolean {
    return window.matchMedia('(pointer: fine)').matches;
  }

  /**
   * Check if device has coarse pointer (finger, basic stylus)
   */
  private supportsCoarsePointer(): boolean {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  /**
   * Get screen size category
   */
  private getScreenSize(): 'small' | 'medium' | 'large' {
    const width = window.innerWidth;

    if (width < 768) return 'small';
    if (width < 1024) return 'medium';
    return 'large';
  }

  /**
   * Get device orientation
   */
  private getOrientation(): 'portrait' | 'landscape' {
    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
  }

  /**
   * Check if device supports touch events
   */
  private supportsTouchEvents(): boolean {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      ((window as any).DocumentTouch && document instanceof (window as any).DocumentTouch)
    );
  }

  /**
   * Get device pixel ratio (high DPI displays are common on laptops)
   */
  private getDevicePixelRatio(): number {
    return window.devicePixelRatio || 1;
  }

  /**
   * Check if Battery API is available (indicates mobile device)
   */
  private supportsBatteryAPI(): boolean {
    return 'getBattery' in navigator || 'battery' in navigator;
  }

  /**
   * Get screen aspect ratio
   */
  private getAspectRatio(): number {
    return window.innerWidth / window.innerHeight;
  }

  /**
   * Get User Agent Client Hints (modern replacement for user agent)
   */
  private getUserAgentHints(): { mobile: boolean; platform: string; model: string } {
    const nav = navigator as any;

    if (nav.userAgentData) {
      return {
        mobile: nav.userAgentData.mobile || false,
        platform: nav.userAgentData.platform || '',
        model: nav.userAgentData.model || ''
      };
    }

    const userAgent = navigator.userAgent;
    return {
      mobile: /Mobile|Android|iPhone/i.test(userAgent),
      platform: this.extractPlatformFromUA(userAgent),
      model: this.extractModelFromUA(userAgent)
    };
  }

  /**
   * Get maximum touch points supported
   */
  private getMaxTouchPoints(): number {
    return navigator.maxTouchPoints || 0;
  }

  /**
   * Extract platform from user agent string
   */
  private extractPlatformFromUA(userAgent: string): string {
    if (/Mac OS X|macOS/i.test(userAgent)) return 'macOS';
    if (/Windows/i.test(userAgent)) return 'Windows';
    if (/Linux/i.test(userAgent)) return 'Linux';
    if (/Android/i.test(userAgent)) return 'Android';
    if (/iOS|iPhone|iPad/i.test(userAgent)) return 'iOS';
    return 'Unknown';
  }

  /**
   * Extract device model from user agent string (mainly for Apple devices)
   */
  private extractModelFromUA(userAgent: string): string {
    // Check for MacBook patterns
    if (/Macintosh.*Intel/i.test(userAgent)) return 'MacBook Intel';
    if (/Macintosh.*PPC/i.test(userAgent)) return 'MacBook PPC';
    if (/Macintosh/i.test(userAgent) && /Safari/i.test(userAgent)) {
      // Modern MacBooks often don't specify architecture in UA
      return 'MacBook';
    }
    return 'Unknown';
  }

  /**
   * Determine device type based on features with intelligent scoring
   */
  private determineDeviceType(features: DeviceInfo['features']): DeviceInfo['type'] {
    const {
      hasHover,
      hasFinePointer,
      hasCoarsePointer,
      screenSize,
      touchSupport,
      devicePixelRatio,
      batteryAPI,
      aspectRatio,
      userAgentHints,
      maxTouchPoints
    } = features;

    const laptopIndicators = this.getLaptopIndicators(features);
    const desktopIndicators = this.getDesktopIndicators(features);

    // Intelligent laptop vs desktop detection based on strength comparison
    if (laptopIndicators >= 2 || desktopIndicators >= 2) {
      // If both have strong indicators, choose the stronger one
      if (laptopIndicators >= 2 && desktopIndicators >= 2) {
        return desktopIndicators > laptopIndicators ? 'desktop' : 'laptop';
      }
      // If only one has strong indicators, choose that one
      return laptopIndicators >= 2 ? 'laptop' : 'desktop';
    }

    // Mobile: Coarse pointer, touch support, small screen
    if (hasCoarsePointer && touchSupport && screenSize === 'small' && userAgentHints.mobile) {
      return 'mobile';
    }

    // Tablet: Touch support, medium+ screen, high touch points
    if (touchSupport && maxTouchPoints > 1 && screenSize !== 'small') {
      return 'tablet';
    }

    // Fallback for laptop vs desktop when both have weak indicators
    if (hasFinePointer && hasHover) {
      // Compare weak indicators - even with scores < 2, prefer the higher score
      if (laptopIndicators > 0 || desktopIndicators > 0) {
        return desktopIndicators > laptopIndicators ? 'desktop' : 'laptop';
      }

      // Ultimate fallback heuristics
      if (batteryAPI || devicePixelRatio > 1.5 || aspectRatio > 1.3) {
        return 'laptop';
      }
      return screenSize === 'large' ? 'desktop' : 'laptop';
    }

    return 'unknown';
  }

  /**
   * Calculate laptop-specific indicators
   */
  private getLaptopIndicators(features: DeviceInfo['features']): number {
    let score = 0;

    // High DPI display (common on modern laptops, especially Apple)
    if (features.devicePixelRatio > 1.5) score++;

    // Battery API availability
    if (features.batteryAPI) score++;

    // MacBook-specific detection
    if (
      features.userAgentHints.platform === 'macOS' &&
      features.userAgentHints.model.includes('MacBook')
    ) {
      score += 2;
    }

    // Typical laptop aspect ratios (16:10, 16:9, 3:2)
    const aspectRatio = features.aspectRatio;
    if (aspectRatio >= 1.3 && aspectRatio <= 1.8) {
      score++;
    }

    // Has hover + fine pointer (trackpad/mouse) but reasonable screen size
    if (
      features.hasHover &&
      features.hasFinePointer &&
      (features.screenSize === 'medium' ||
        (features.screenSize === 'large' && features.devicePixelRatio > 1.5))
    ) {
      score++;
    }

    // Limited or no touch points (most laptops have 0, some have 1-2)
    if (features.maxTouchPoints <= 2) {
      score++;
    }

    return score;
  }

  /**
   * Calculate desktop-specific indicators
   */
  private getDesktopIndicators(features: DeviceInfo['features']): number {
    let score = 0;

    // Very large screen with standard DPI (external monitor)
    if (features.screenSize === 'large' && features.devicePixelRatio <= 1.25) {
      score++;
    }

    // No battery API (desktop computers)
    if (!features.batteryAPI) score++;

    // Ultra-wide aspect ratios (external monitors)
    if (features.aspectRatio > 2.0 || features.aspectRatio < 1.2) {
      score++;
    }

    // Windows platform with large screen often indicates desktop
    if (
      features.userAgentHints.platform === 'Windows' &&
      features.screenSize === 'large' &&
      features.devicePixelRatio <= 1.5
    ) {
      score++;
    }

    // No touch support at all
    if (!features.touchSupport && features.maxTouchPoints === 0) {
      score++;
    }

    return score;
  }

  /**
   * Calculate confidence level based on feature consistency
   */
  private calculateConfidence(
    features: DeviceInfo['features'],
    type: DeviceInfo['type']
  ): 'high' | 'medium' | 'low' {
    let score = 0;

    // High confidence indicators
    if (
      type === 'desktop' &&
      features.hasHover &&
      features.hasFinePointer &&
      !features.touchSupport
    ) {
      score += 3;
    }

    if (type === 'laptop' && features.hasHover && features.hasFinePointer) {
      score += 2;
    }

    if (
      type === 'mobile' &&
      features.hasCoarsePointer &&
      features.touchSupport &&
      features.screenSize === 'small'
    ) {
      score += 3;
    }

    // Screen size consistency
    if (
      (type === 'desktop' && features.screenSize === 'large') ||
      (type === 'laptop' &&
        (features.screenSize === 'medium' || features.screenSize === 'large')) ||
      (type === 'mobile' && features.screenSize === 'small')
    ) {
      score += 1;
    }

    // User agent consistency (additional check)
    if (this.getUserAgentDeviceType() === type) {
      score += 1;
    }

    if (score >= 4) return 'high';
    if (score >= 2) return 'medium';
    return 'low';
  }

  /**
   * Fallback user agent detection (less reliable but useful for confidence scoring)
   */
  private getUserAgentDeviceType(): DeviceInfo['type'] {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/mobile|android|iphone|ipad|tablet/i.test(userAgent)) {
      if (/ipad|tablet/i.test(userAgent)) return 'tablet';
      return 'mobile';
    }

    if (/laptop/i.test(userAgent)) return 'laptop';

    return 'desktop';
  }

  /**
   * Setup event listeners for dynamic updates
   */
  public setupDynamicDetection(callback: (deviceInfo: DeviceInfo) => void): () => void {
    const handleChange = () => {
      this.deviceInfo = null; // Reset cache
      callback(this.detectDevice());
    };

    // Listen for orientation changes
    window.addEventListener('orientationchange', handleChange);
    window.addEventListener('resize', handleChange);

    // Return cleanup function
    return () => {
      window.removeEventListener('orientationchange', handleChange);
      window.removeEventListener('resize', handleChange);
    };
  }

  /**
   * Utility method to check if current device is laptop
   */
  public isLaptop(): boolean {
    return this.detectDevice().type === 'laptop';
  }

  /**
   * Utility method to check if current device is desktop
   */
  public isDesktop(): boolean {
    return this.detectDevice().type === 'desktop';
  }
}

// Singleton instance for easy access
export const deviceDetector = DeviceDetector.getInstance();

// Convenience function to get current device info
export const deviceInfo = (): DeviceInfo => deviceDetector.detectDevice();

/**
 * React hook for device detection with dynamic updates
 * Provides device information and automatically updates on orientation/resize changes
 */
export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => deviceDetector.detectDevice());

  useEffect(() => {
    // Set up dynamic detection with cleanup
    const cleanup = deviceDetector.setupDynamicDetection((newDeviceInfo) => {
      setDeviceInfo(newDeviceInfo);
    });

    // Cleanup on unmount
    return cleanup;
  }, []);

  return deviceInfo;
};

/**
 * Utility function to get device-specific CSS classes
 * Useful for conditional styling based on device type
 */
export const getDeviceStyles = (deviceInfo?: DeviceInfo) => {
  const info = deviceInfo || deviceDetector.detectDevice();

  return {
    deviceType: `device-${info.type}`,
    confidence: `confidence-${info.confidence}`,
    screenSize: `screen-${info.features.screenSize}`,
    orientation: `orientation-${info.features.orientation}`,
    hasHover: info.features.hasHover ? 'has-hover' : 'no-hover',
    hasFinePointer: info.features.hasFinePointer ? 'fine-pointer' : 'coarse-pointer',
    hasCoarsePointer: info.features.hasCoarsePointer ? 'coarse-pointer' : 'no-coarse-pointer',
    touchSupport: info.features.touchSupport ? 'touch-enabled' : 'touch-disabled',
    batteryAPI: info.features.batteryAPI ? 'battery-api' : 'no-battery-api',
    devicePixelRatio: `dpr-${Math.round(info.features.devicePixelRatio * 100)}`,
    aspectRatio: `aspect-${info.features.aspectRatio.toFixed(2)}`,
    platform: `platform-${info.features.userAgentHints.platform.toLowerCase()}`,
    model: info.features.userAgentHints.model
      ? `model-${info.features.userAgentHints.model.replace(/\s+/g, '-').toLowerCase()}`
      : 'model-unknown',
    maxTouchPoints: `touchpoints-${info.features.maxTouchPoints}`
  };
};

/**
 * Convert DeviceInfo to legacy format used by existing systems
 * This bridges the new DeviceDetector with existing code that expects OperatingSystem and MachineType enums
 */
export const convertDeviceInfoToMachineInfo = (
  deviceInfo: DeviceInfo
): {
  os: OperatingSystem;
  machineType: MachineType;
  confidence: 'high' | 'medium' | 'low';
} => {
  // Map device type to machine type
  let machineType: MachineType;
  switch (deviceInfo.type) {
    case 'desktop':
      machineType = MachineType.MACHINE_TYPE_DESKTOP;
      break;
    case 'laptop':
      machineType = MachineType.MACHINE_TYPE_LAPTOP;
      break;
    case 'tablet':
    case 'mobile':
      machineType = MachineType.MACHINE_TYPE_LAPTOP;
      break;
    default:
      machineType = MachineType.MACHINE_TYPE_DESKTOP;
  }

  // Map platform to OS
  let os: OperatingSystem;
  const platform = deviceInfo.features.userAgentHints.platform.toLowerCase();

  if (platform.includes('windows')) {
    os = OperatingSystem.OS_WINDOWS;
  } else if (platform.includes('linux')) {
    os = OperatingSystem.OS_LINUX;
  } else if (platform.includes('macos') || platform.includes('mac')) {
    os = OperatingSystem.OS_MAC;
  } else if (platform.includes('android')) {
    os = OperatingSystem.OS_LINUX; // Treat Android as Linux-based
  } else if (platform.includes('ios')) {
    os = OperatingSystem.OS_MAC; // Treat iOS as Mac-based
  } else {
    os = OperatingSystem.OS_MAC; // Default fallback
  }

  return {
    os,
    machineType,
    confidence: deviceInfo.confidence
  };
};

// Export both named and default for flexibility:
// - Named export allows importing as { DeviceDetector } for tree-shaking or explicit usage.
// - Default export allows importing as any name, e.g., import DeviceDetector from '...'.
export { DeviceDetector };
export default DeviceDetector;
